import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { apiPost, apiPut } from '../../../api/client';
import { Button } from '../../../components/ui/Button';
import { Modal } from '../../../components/ui/Modal';
import { IconPicker } from '../../../components/ui/IconPicker';
import { formatCurrency } from '../../../utils/format';
import type { ProductTemplate, AttributeType } from '../../../api/types';
import { useProductCategories } from '../../../api/useProductCategories';
import { AttributeRow, emptyAttr, type AttrForm } from './AttributeRow';

// ─── Shared styles ───────────────────────────────────────────────────────

const inpSt: React.CSSProperties = {
  background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 'var(--radius-card)', color: 'var(--color-text)',
  fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem', outline: 'none', width: '100%',
};

const selSt: React.CSSProperties = {
  background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 'var(--radius-card)', color: 'var(--color-text)',
  fontSize: 'var(--text-sm)', padding: '0.5rem 0.625rem', outline: 'none',
  cursor: 'pointer', width: '100%',
};

// ─── Helpers ─────────────────────────────────────────────────────────────

function marginPct(baseCost: number, basePrice: number): number {
  if (basePrice <= 0) return 0;
  return ((basePrice - baseCost) / basePrice) * 100;
}

interface TemplateFormData {
  name: string;
  category: string;
  icon: string;
  description: string;
  unit: string;
  baseCost: string;
  basePrice: string;
  isActive: boolean;
  attributes: AttrForm[];
}

const emptyForm = (defaultCategorySlug: string): TemplateFormData => ({
  name: '', category: defaultCategorySlug, icon: 'Package', description: '', unit: '', baseCost: '0', basePrice: '0', isActive: true, attributes: [],
});

function fromTemplate(t: ProductTemplate): TemplateFormData {
  return {
    name: t.name,
    category: t.category,
    icon: t.icon ?? 'Package',
    description: t.description ?? '',
    unit: t.unit ?? '',
    baseCost: String(t.baseCost),
    basePrice: String(t.basePrice),
    isActive: t.isActive,
    attributes: t.attributes.map(a => ({
      key: a.key,
      attributeType: a.attributeType,
      defaultValue: a.defaultValue ?? '',
      options: (() => {
        const raw = a.options ?? '';
        if (!raw) return '';
        const s = raw.trim();
        if (s.startsWith('[')) { try { return (JSON.parse(s) as string[]).join('\n'); } catch {} }
        return raw;
      })(),
      unit: a.unit ?? '',
      isRequired: a.isRequired,
      sortOrder: a.sortOrder,
    })),
  };
}

// ─── TemplateModal ───────────────────────────────────────────────────────

export function TemplateModal({
  open, onClose, editing,
}: {
  open: boolean; onClose: () => void; editing: ProductTemplate | null;
}) {
  const qc = useQueryClient();
  const { data: categories = [] } = useProductCategories();
  const defaultCategorySlug = categories.length > 0 ? categories[0].slug : 'altro';
  const [form, setForm] = useState<TemplateFormData>(editing ? fromTemplate(editing) : emptyForm(defaultCategorySlug));
  const [prevEditing, setPrevEditing] = useState(editing);

  if (prevEditing !== editing) {
    setPrevEditing(editing);
    setForm(editing ? fromTemplate(editing) : emptyForm(defaultCategorySlug));
  }

  const upd = <K extends keyof TemplateFormData>(key: K, val: TemplateFormData[K]) =>
    setForm(f => ({ ...f, [key]: val }));

  const cost  = Number(form.baseCost)  || 0;
  const price = Number(form.basePrice) || 0;
  const margin = marginPct(cost, price);
  const marginAbs = price - cost;

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        icon: form.icon,
        description: form.description.trim() || null,
        unit: form.unit.trim() || null,
        baseCost: cost,
        basePrice: price,
        isActive: form.isActive,
        attributes: form.attributes.map((a, i) => ({
          key: a.key.trim(),
          attributeType: a.attributeType,
          defaultValue: a.defaultValue || null,
          options: a.attributeType === 'SELECT'
            ? JSON.stringify(a.options.split('\n').map(s => s.trim()).filter(Boolean)) || null
            : null,
          unit: a.attributeType === 'NUMBER' ? (a.unit.trim() || null) : null,
          isRequired: a.isRequired,
          sortOrder: i,
        })),
      };
      return editing
        ? apiPut(`/product-templates/${editing.id}`, payload)
        : apiPost('/product-templates', payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-templates'] });
      toast.success(editing ? 'Prodotto aggiornato' : 'Prodotto creato');
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const addAttr = () => upd('attributes', [...form.attributes, emptyAttr(form.attributes.length)]);

  const removeAttr = (i: number) =>
    upd('attributes', form.attributes.filter((_, idx) => idx !== i));

  const updateAttr = (i: number, attr: AttrForm) =>
    upd('attributes', form.attributes.map((a, idx) => idx === i ? attr : a));

  const moveAttr = (i: number, dir: 'up' | 'down') => {
    const arr = [...form.attributes];
    const j = dir === 'up' ? i - 1 : i + 1;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    upd('attributes', arr);
  };

  const canSave = form.name.trim().length > 0 && form.attributes.every(a => a.key.trim().length > 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Modifica: ${editing.name}` : 'Nuovo prodotto'}
      width={680}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button onClick={() => saveMutation.mutate()} loading={saveMutation.isPending} disabled={!canSave}>
            {editing ? 'Salva' : 'Crea'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        {/* Nome + Icona + categoria */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 160px', gap: '0.75rem' }}>
          <div>
            <label style={{ fontSize: 'var(--text-xs)', fontWeight: 500, color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.375rem' }}>Nome *</label>
            <input value={form.name} onChange={e => upd('name', e.target.value)} placeholder="es. Album 30x30" style={inpSt} />
          </div>
          <div>
            <label style={{ fontSize: 'var(--text-xs)', fontWeight: 500, color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.375rem' }}>Icona</label>
            <IconPicker value={form.icon} onChange={v => upd('icon', v)} />
          </div>
          <div>
            <label style={{ fontSize: 'var(--text-xs)', fontWeight: 500, color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.375rem' }}>Categoria *</label>
            <select value={form.category} onChange={e => upd('category', e.target.value)} style={selSt}>
              {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
        </div>

        {/* Descrizione + unita */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: '0.75rem' }}>
          <div>
            <label style={{ fontSize: 'var(--text-xs)', fontWeight: 500, color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.375rem' }}>Descrizione</label>
            <input value={form.description} onChange={e => upd('description', e.target.value)} placeholder="Descrizione opzionale" style={inpSt} />
          </div>
          <div>
            <label style={{ fontSize: 'var(--text-xs)', fontWeight: 500, color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.375rem' }}>Unita</label>
            <input value={form.unit} onChange={e => upd('unit', e.target.value)} placeholder="pezzo / ora..." style={inpSt} />
          </div>
        </div>

        {/* Costo + Prezzo + Margine live */}
        <div>
          <label style={{ fontSize: 'var(--text-xs)', fontWeight: 500, color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.375rem' }}>
            Costo & Prezzo
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', display: 'block', marginBottom: '0.25rem' }}>Costo (laboratorio/fornitore) EUR</label>
              <input type="number" min="0" step="0.01" value={form.baseCost}
                onChange={e => upd('baseCost', e.target.value)} style={inpSt} />
            </div>
            <div>
              <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', display: 'block', marginBottom: '0.25rem' }}>Prezzo listino cliente EUR</label>
              <input type="number" min="0" step="0.01" value={form.basePrice}
                onChange={e => upd('basePrice', e.target.value)} style={inpSt} />
            </div>
          </div>
          {/* Margine live */}
          <div style={{
            marginTop: '0.5rem', padding: '0.5rem 0.75rem',
            background: margin >= 0 ? 'oklch(from var(--color-success) l c h / 0.08)' : 'oklch(from var(--color-error) l c h / 0.08)',
            border: `1px solid ${margin >= 0 ? 'oklch(from var(--color-success) l c h / 0.25)' : 'oklch(from var(--color-error) l c h / 0.25)'}`,
            borderRadius: 'var(--radius-interactive)',
            display: 'flex', alignItems: 'center', gap: '0.5rem',
          }}>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Margine:</span>
            <span style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: margin >= 0 ? 'var(--color-success)' : 'var(--color-error)' }}>
              {formatCurrency(marginAbs)} ({margin.toFixed(1)}%)
            </span>
          </div>
        </div>

        {/* Attributi */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.625rem' }}>
            <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Attributi custom ({form.attributes.length})
            </label>
            <Button size="sm" variant="secondary" icon={<Plus size={12} />} onClick={addAttr}>
              Aggiungi attributo
            </Button>
          </div>
          {form.attributes.length === 0 && (
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', fontStyle: 'italic' }}>
              Nessun attributo — il prodotto verra usato senza configurazione aggiuntiva.
            </p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {form.attributes.map((attr, i) => (
              <AttributeRow
                key={i} attr={attr} index={i} total={form.attributes.length}
                onChange={a => updateAttr(i, a)}
                onRemove={() => removeAttr(i)}
                onMove={dir => moveAttr(i, dir)}
              />
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
